'use client';

import { useTranslation } from 'next-i18next';
import { Button, Anchor } from '@components/common';
import { useSidebarNavigation } from '@components/contexts/SidebarNavigationContext';
import { useMissionControlContext } from '@hooks/features/useMissionControlContext';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { getI18nLinkHref } from '@services/i18nPaths';
import { FloatingNavBar } from './FloatingNavBar';
import { FloatingNavBarOverflowMenu } from './FloatingNavBarOverflowMenu';

export type CandidateNavButton = 'new-job' | 'coaching' | 'edit-profile';

const SELECTED_CLASS = '!border !border-ink !bg-surface !text-ink hover:!border-ink hover:!bg-surface hover:!text-ink focus:!ring-0 !cursor-default';
const DEFAULT_CLASS = '!border !border-input !bg-surface !text-ink hover:!border-ink hover:!bg-surface-50 hover:!text-ink focus:!ring-0';
const BTN_CLASS = '!rounded-2xl !px-5 !py-3 !text-lg !shadow-md hover:!no-underline whitespace-nowrap';


/**
 * @description Candidate-specific floating nav bar shown on all candidate MC views.
 *
 * Buttons (left → right):
 * - **Coaching** — placeholder, navigates to career coaching (TBD).
 * - **New Job** (promo) — navigates to the candidate job search form.
 *   Hidden only while the search form is visible (the form has its own submit button).
 *   Remains visible on the job results page so the user can start a new search.
 * - **Edit Profile** — opens the CV/profile edit page.
 * - **Switch to hire** — mirrors the sidebar context switch (works for guests too).
 *
 * Pass `selectedButton` to mark a button as the current page (adds border, disables click).
 */
export const CandidateFloatingNavBar = ({
  locale,
  selectedButton,
  pillBackground,
  inline,
  isAuthenticated = false,
}: {
  locale: Locale;
  selectedButton?: CandidateNavButton;
  pillBackground?: 'surface' | 'surface-100';
  inline?: boolean;
  isAuthenticated?: boolean;
}) => {
  const { t } = useTranslation('common');
  const { currentView, navigateToNewJobSearchForm } = useSidebarNavigation();
  const { setContext } = useMissionControlContext();
  const { router } = useExtendedRouter();

  const isSearchForm = currentView === 'new-job-search-form'
    || (typeof currentView === 'object' && 'editSearch' in currentView);

  const editProfileHref = isAuthenticated
    ? `/${locale}/profile`
    : `/login?_target_path=${encodeURIComponent(`/${locale}/profile`)}`;

  const handleSwitchToHire = () => {
    setContext('company');

    // Already on /mc: the context change re-renders MissionControl in place via the
    // dispatched mc-context-change event. router.push would do a full page reload
    // (useExtendedRouter falls back to window.location.assign in App Router),
    // which briefly flashes the previous view. Clean any search/hash via pushState.
    // On dedicated /mc/* pages, navigate so the user actually lands on MC.
    const mcPath = getI18nLinkHref('/mc', locale);
    if (typeof window === 'undefined') return;

    if (window.location.pathname === mcPath) {
      if (window.location.search || window.location.hash) {
        window.history.pushState(null, '', mcPath);
      }
      return;
    }

    void router.push('/mc');
  };

  return (
    <FloatingNavBar pillBackground={pillBackground} inline={inline}>
      <FloatingNavBarOverflowMenu>
        {/* Coaching */}
        {selectedButton === 'coaching' ? (
          <Button
            type="button"
            styling="solid"
            color="light"
            className={`${BTN_CLASS} ${SELECTED_CLASS}`}
          >
            {t('mc.career.coaching')}
          </Button>
        ) : (
          <Anchor
            href={`/${locale}`}
            styling="solid"
            color="light"
            className={`${BTN_CLASS} ${DEFAULT_CLASS}`}
          >
            {t('mc.career.coaching')}
          </Anchor>
        )}

        {/* New Job — shown as selected (non-interactive) while the search form is visible */}
        {isSearchForm ? (
          <Button
            type="button"
            styling="solid"
            color="light"
            className={`${BTN_CLASS} ${SELECTED_CLASS}`}
          >
            {t('mc.search.job')}
          </Button>
        ) : (
          <Button
            type="button"
            styling="solid"
            color="light"
            className={`${BTN_CLASS} ${DEFAULT_CLASS}`}
            onClick={navigateToNewJobSearchForm}
          >
            {t('mc.search.job')}
          </Button>
        )}

        {/* Edit Profile */}
        {selectedButton === 'edit-profile' ? (
          <Button
            type="button"
            styling="solid"
            color="light"
            className={`${BTN_CLASS} ${SELECTED_CLASS}`}
          >
            {t('global.label.cv.edit')}
          </Button>
        ) : (
          <Anchor
            href={editProfileHref}
            styling="solid"
            color="light"
            className={`${BTN_CLASS} ${DEFAULT_CLASS}`}
          >
            {t('global.label.cv.edit')}
          </Anchor>
        )}

        {/* Context Switch — mirrors the sidebar switch (go to hiring). */}
        <Button
          type="button"
          styling="solid"
          color="light"
          className={`${BTN_CLASS} ${DEFAULT_CLASS}`}
          onClick={handleSwitchToHire}
        >
          {t('mc.for.employers')}
        </Button>
      </FloatingNavBarOverflowMenu>
    </FloatingNavBar>
  );
};
